import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import AddIcon from '@material-ui/icons/Add';
import CenterFocusStrongIcon from '@material-ui/icons/CenterFocusStrong';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import EditLocationIcon from '@material-ui/icons/EditLocation';
import GetAppIcon from '@material-ui/icons/GetApp';
import RefreshIcon from '@material-ui/icons/Refresh';
import WarningIcon from '@material-ui/icons/Warning';
import CallSplitIcon from '@material-ui/icons/CallSplit';
import SplitPropertyBoundaryDialog from '../group/SplitPropertyBoundaryDialog';

const useStyles = makeStyles(() => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 10,
  },
  title: { fontSize: 14, fontWeight: 500, flex: 1 },
  panel: {
    border: '1px solid #dbe7f3',
    borderRadius: 8,
    background: '#f8fbff',
    padding: 12,
  },
  warning: {
    display: 'flex',
    gap: 7,
    alignItems: 'flex-start',
    color: '#8a4b08',
    background: '#fff8e1',
    border: '1px solid #ffe0a3',
    borderRadius: 6,
    padding: '7px 8px',
    marginBottom: 8,
    fontSize: 11,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '7px 0',
    borderBottom: '1px solid #e8edf2',
  },
  rowText: { flex: 1, minWidth: 0 },
  primary: {
    fontSize: 12,
    fontWeight: 600,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  secondary: {
    fontSize: 10,
    color: '#777',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  area: {
    marginTop: 8,
    padding: 9,
    borderRadius: 6,
    background: '#fff8df',
    borderLeft: '3px solid #f28c00',
  },
  areaActions: { display: 'flex', alignItems: 'center', gap: 2 },
  metrics: { fontSize: 10, color: '#666', marginTop: 4 },
  searchResult: {
    width: '100%',
    textAlign: 'left',
    justifyContent: 'flex-start',
    textTransform: 'none',
    padding: '8px 10px',
    marginBottom: 5,
  },
  importRow: {
    display: 'flex',
    alignItems: 'center',
    borderBottom: '1px solid #eceff1',
    padding: '5px 0',
  },
}));

const clientIdOf = value => String(value?._id || value || '');
const memberIdOf = member => String(member?.jobSiteId?._id || member?.jobSiteId || '');
const formatSf = value => Math.round(Number(value || 0)).toLocaleString();

const OperationalPropertyGroupSection = ({
  controller,
  currentJobsiteId,
  onDrawArea,
  onEditAreaShape,
  onDrawMemberBoundary,
  drawing,
  editing,
  disabled,
}) => {
  const classes = useStyles();
  const [createOpen, setCreateOpen] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importCandidates, setImportCandidates] = useState([]);
  const [selectedImportIds, setSelectedImportIds] = useState([]);
  const [detailsArea, setDetailsArea] = useState(null);
  const [details, setDetails] = useState(null);
  const [splitBoundaryOpen, setSplitBoundaryOpen] = useState(false);

  const group = controller?.group;
  const workspace = controller?.workspace;
  const members = group?.members || [];
  const warnings = workspace?.warnings || group?.warnings || [];
  const boundaries = workspace?.boundaries || [];
  const boundaryByJobSiteId = useMemo(
    () => new Map(boundaries.map(boundary => [String(boundary.jobSiteId), boundary])),
    [boundaries]
  );
  const duplicateBoundaryWarning = warnings.find(
    warning => warning.code === 'DUPLICATE_PROPERTY_BOUNDARY'
  );
  const slicesByArea = useMemo(() => {
    const result = new Map();
    (workspace?.slices || []).forEach(slice => {
      const key = String(slice.groupAreaId);
      if (!result.has(key)) result.set(key, []);
      result.get(key).push(slice);
    });
    return result;
  }, [workspace]);
  const importGroups = useMemo(() => {
    const grouped = new Map();
    importCandidates.forEach(area => {
      const key = String(area.sourceJobSiteId);
      if (!grouped.has(key)) {
        grouped.set(key, {
          jobSiteId: key,
          propertyName: area.propertyName || 'Property',
          clientName: area.clientName || '',
          areas: [],
        });
      }
      grouped.get(key).areas.push(area);
    });
    return [...grouped.values()];
  }, [importCandidates]);

  useEffect(() => {
    if (!detailsArea) {
      setDetails(null);
      return;
    }
    setDetails({
      name: detailsArea.name || '',
      areaType: detailsArea.areaType || '',
      servicesRequired: (detailsArea.servicesRequired || []).join(', '),
      productionRateValue: detailsArea.productionRate?.value ?? '',
      productionRateUnit: detailsArea.productionRate?.unit || 'sf_per_hour',
      includedJobSiteIds: (detailsArea.includedJobSiteIds || []).map(String),
    });
  }, [detailsArea]);

  const handleCreate = async () => {
    const created = await controller.createGroup(groupName.trim() || 'Property group');
    if (created) {
      setCreateOpen(false);
      setGroupName('');
    }
  };

  const handleSearch = async () => {
    setSearching(true);
    setResults(await controller.searchJobsites(query));
    setSearching(false);
  };

  const handleAdd = async candidate => {
    const existingClients = new Set(members.map(member => String(member.clientId || '')).filter(Boolean));
    const candidateClientId = clientIdOf(candidate.client);
    if (
      existingClients.size &&
      candidateClientId &&
      !existingClients.has(candidateClientId) &&
      !window.confirm('This property belongs to a different customer. Add it to the operational group anyway?')
    ) return;
    const added = await controller.addMember(candidate);
    if (added) {
      setAddOpen(false);
      setQuery('');
      setResults([]);
    }
  };

  const handleOpenImport = async () => {
    setImportOpen(true);
    setImportLoading(true);
    const candidates = await controller.loadImportableAreas();
    setImportCandidates(candidates);
    setSelectedImportIds(
      candidates.filter(area => !area.alreadyImported).map(area => String(area.sourceAreaId))
    );
    setImportLoading(false);
  };

  const handleImport = async () => {
    const imported = await controller.importAreas(selectedImportIds);
    if (imported) {
      setImportOpen(false);
      setImportCandidates([]);
      setSelectedImportIds([]);
    }
  };

  const handleSaveDetails = async () => {
    if (!detailsArea || !details) return;
    const rateValue = Number(details.productionRateValue);
    const saved = await controller.updateArea(detailsArea._id, {
      name: details.name.trim() || 'Group service area',
      areaType: details.areaType.trim(),
      servicesRequired: details.servicesRequired
        .split(',')
        .map(item => item.trim())
        .filter(Boolean),
      productionRate: Number.isFinite(rateValue) && rateValue > 0
        ? { value: rateValue, unit: details.productionRateUnit || 'sf_per_hour' }
        : null,
      includedJobSiteIds: details.includedJobSiteIds,
    });
    if (saved) setDetailsArea(null);
  };

  const existingIds = new Set(members.map(memberIdOf));

  return (
    <Box>
      <div className={classes.header}>
        <AccountTreeIcon style={{ fontSize: 18, color: '#1565c0' }} />
        <Typography className={classes.title}>Property group</Typography>
        {group && (
          <Tooltip title="Fit all group properties">
            <IconButton size="small" onClick={controller.fitToGroup}>
              <CenterFocusStrongIcon style={{ fontSize: 17 }} />
            </IconButton>
          </Tooltip>
        )}
      </div>

      {controller?.loading && !group ? (
        <Box display="flex" alignItems="center" style={{ gap: 8, color: '#777', fontSize: 12 }}>
          <CircularProgress size={14} /> Loading groups…
        </Box>
      ) : !group ? (
        <div className={classes.panel}>
          <Typography style={{ fontSize: 12, color: '#555', marginBottom: 8 }}>
            Combine neighboring properties so natural work areas can cross their boundaries.
          </Typography>
          <Button
            size="small"
            variant="outlined"
            color="primary"
            onClick={() => setCreateOpen(true)}
            disabled={disabled}
          >
            Create property group
          </Button>
        </div>
      ) : (
        <div className={classes.panel}>
          {controller.groups?.length > 1 ? (
            <Select
              fullWidth
              value={controller.activeGroupId || ''}
              onChange={event => controller.setActiveGroupId(event.target.value)}
              style={{ fontSize: 13, marginBottom: 8 }}
            >
              {controller.groups.map(option => (
                <MenuItem key={option._id} value={option._id}>{option.name}</MenuItem>
              ))}
            </Select>
          ) : (
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography style={{ fontSize: 13, fontWeight: 600 }}>{group.name}</Typography>
              <Chip size="small" label={`${members.length} ${members.length === 1 ? 'property' : 'properties'}`} />
            </Box>
          )}

          {warnings.map((warning, index) => (
            <div className={classes.warning} key={`${warning.code}-${warning.jobSiteId || index}`}>
              <WarningIcon style={{ fontSize: 15, flexShrink: 0 }} />
              <span>{warning.message}</span>
            </div>
          ))}

          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography style={{ fontSize: 11, fontWeight: 700, color: '#555' }}>MEMBERS</Typography>
            <Box display="flex" style={{ gap: 4 }}>
              {duplicateBoundaryWarning && (
                <Button
                  size="small"
                  startIcon={<CallSplitIcon style={{ fontSize: 14 }} />}
                  onClick={() => setSplitBoundaryOpen(true)}
                  disabled={controller.saving || drawing || editing}
                  style={{ textTransform: 'none', fontSize: 11 }}
                >
                  Split shared parcel
                </Button>
              )}
              <Button
                size="small"
                startIcon={<AddIcon style={{ fontSize: 14 }} />}
                onClick={() => setAddOpen(true)}
                disabled={controller.saving}
                style={{ textTransform: 'none', fontSize: 11 }}
              >
                Add property
              </Button>
            </Box>
          </Box>

          {members.map(member => {
            const memberId = memberIdOf(member);
            const boundary = boundaryByJobSiteId.get(memberId);
            const boundaryLabel = boundary
              ? `${boundary.source === 'manual' ? 'Manual' : 'County'} boundary`
              : 'No boundary';
            return (
              <div className={classes.row} key={memberId}>
                <div className={classes.rowText}>
                  <div className={classes.primary}>{member.propertyName || 'Property'}</div>
                  <div className={classes.secondary}>
                    {member.clientName || 'No customer'} · {boundaryLabel}
                  </div>
                </div>
                <Tooltip title={boundary ? 'Draw replacement manual boundary' : 'Draw manual boundary'}>
                  <IconButton
                    size="small"
                    onClick={() => onDrawMemberBoundary(member)}
                    disabled={controller.saving || drawing || editing}
                  >
                    <EditLocationIcon style={{ fontSize: 16, color: '#2e7d32' }} />
                  </IconButton>
                </Tooltip>
                {memberId !== String(currentJobsiteId) && (
                  <Tooltip title="Remove from group">
                    <IconButton
                      size="small"
                      onClick={() => {
                        if (window.confirm(`Remove "${member.propertyName}" from this group?`)) {
                          controller.removeMember(memberId);
                        }
                      }}
                    >
                      <DeleteIcon style={{ fontSize: 15, color: '#888' }} />
                    </IconButton>
                  </Tooltip>
                )}
              </div>
            );
          })}

          <Box display="flex" justifyContent="space-between" alignItems="center" mt={1.5}>
            <Typography style={{ fontSize: 11, fontWeight: 700, color: '#555' }}>GROUP WORK AREAS</Typography>
            <Box display="flex" style={{ gap: 5 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<GetAppIcon style={{ fontSize: 14 }} />}
                onClick={handleOpenImport}
                disabled={drawing || editing || controller.saving}
                style={{ textTransform: 'none', fontSize: 11 }}
              >
                Auto import
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon style={{ fontSize: 14 }} />}
                onClick={onDrawArea}
                disabled={drawing || editing || controller.saving}
                style={{ textTransform: 'none', fontSize: 11 }}
              >
                {drawing ? 'Drawing…' : 'Draw'}
              </Button>
            </Box>
          </Box>

          {(workspace?.areas || []).length === 0 && (
            <Typography style={{ fontSize: 11, color: '#888', marginTop: 8 }}>
              Draw a natural work area across the member property lines.
            </Typography>
          )}

          {(workspace?.areas || []).map(area => {
            const slices = slicesByArea.get(String(area._id)) || [];
            const includedSf = slices
              .filter(slice => slice.allocationStatus === 'included')
              .reduce((sum, slice) => sum + Number(slice.squareFeet || 0), 0);
            const excludedCount = slices.filter(slice => slice.allocationStatus === 'excluded').length;
            const unallocatedSf = slices
              .filter(slice => slice.allocationStatus === 'unallocated')
              .reduce((sum, slice) => sum + Number(slice.squareFeet || 0), 0);
            return (
              <div className={classes.area} key={area._id}>
                <Box display="flex" alignItems="center">
                  <div className={classes.rowText}>
                    <div className={classes.primary}>{area.name || 'Group service area'}</div>
                    <div className={classes.secondary}>
                      {area.areaType || 'No area type'} · shared rate
                    </div>
                  </div>
                  <div className={classes.areaActions}>
                    <Tooltip title="Edit shared details and property inclusion">
                      <IconButton size="small" onClick={() => setDetailsArea(area)}>
                        <EditIcon style={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit canonical shape">
                      <IconButton
                        size="small"
                        onClick={() => onEditAreaShape(area)}
                        disabled={drawing || editing}
                      >
                        <EditLocationIcon style={{ fontSize: 15, color: '#f28c00' }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Recalculate slices">
                      <IconButton size="small" onClick={() => controller.recalculateArea(area._id)}>
                        <RefreshIcon style={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Archive area">
                      <IconButton
                        size="small"
                        onClick={() => {
                          if (window.confirm(`Archive "${area.name}"?`)) controller.deleteArea(area._id);
                        }}
                      >
                        <DeleteIcon style={{ fontSize: 15, color: '#888' }} />
                      </IconButton>
                    </Tooltip>
                  </div>
                </Box>
                <div className={classes.metrics}>
                  Included {formatSf(includedSf)} SF
                  {excludedCount ? ` · ${excludedCount} excluded ${excludedCount === 1 ? 'slice' : 'slices'}` : ''}
                  {unallocatedSf > 0.5 ? ` · ${formatSf(unallocatedSf)} SF unallocated` : ''}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!!controller?.error && (
        <Typography style={{ color: '#b71c1c', fontSize: 11, marginTop: 6 }}>
          {controller.error}
        </Typography>
      )}

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Create property group</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Group name"
            value={groupName}
            onChange={event => setGroupName(event.target.value)}
            placeholder="e.g. Main Event campus"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button color="primary" variant="contained" onClick={handleCreate} disabled={controller?.saving}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add neighboring property</DialogTitle>
        <DialogContent>
          <Box display="flex" style={{ gap: 8, marginBottom: 12 }}>
            <TextField
              autoFocus
              fullWidth
              label="Property name or address"
              value={query}
              onChange={event => setQuery(event.target.value)}
              onKeyDown={event => {
                if (event.key === 'Enter') handleSearch();
              }}
            />
            <Button variant="outlined" onClick={handleSearch} disabled={!query.trim() || searching}>
              {searching ? <CircularProgress size={16} /> : 'Search'}
            </Button>
          </Box>
          {results
            .filter(candidate => !existingIds.has(String(candidate._id)))
            .map(candidate => (
              <Button
                key={candidate._id}
                className={classes.searchResult}
                variant="outlined"
                onClick={() => handleAdd(candidate)}
              >
                <span>
                  <strong>{candidate.name || candidate.address}</strong><br />
                  <span style={{ color: '#777', fontSize: 11 }}>
                    {[candidate.address, candidate.city, candidate.state].filter(Boolean).join(', ')}
                    {candidate.client?.companyName ? ` · ${candidate.client.companyName}` : ''}
                  </span>
                </span>
              </Button>
            ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={importOpen} onClose={() => setImportOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Auto import existing areas</DialogTitle>
        <DialogContent>
          <Typography style={{ fontSize: 12, color: '#666', marginBottom: 10 }}>
            Select measured polygons from each property’s latest Site Takeoff. Imported areas
            retain their details and are automatically sliced at the group’s property boundaries.
          </Typography>
          {importLoading ? (
            <Box display="flex" alignItems="center" style={{ gap: 8, padding: '18px 0' }}>
              <CircularProgress size={17} />
              <Typography style={{ fontSize: 12 }}>Finding existing areas…</Typography>
            </Box>
          ) : importGroups.length === 0 ? (
            <Typography style={{ fontSize: 12, color: '#777', padding: '12px 0' }}>
              No polygon areas were found in the latest Site Takeoffs for these properties.
            </Typography>
          ) : (
            <>
              <Box display="flex" justifyContent="flex-end">
                <Button
                  size="small"
                  onClick={() => {
                    const available = importCandidates
                      .filter(area => !area.alreadyImported)
                      .map(area => String(area.sourceAreaId));
                    setSelectedImportIds(
                      selectedImportIds.length === available.length ? [] : available
                    );
                  }}
                  style={{ textTransform: 'none', fontSize: 11 }}
                >
                  {selectedImportIds.length ? 'Clear selection' : 'Select all new'}
                </Button>
              </Box>
              {importGroups.map(property => (
                <Box key={property.jobSiteId} mb={2}>
                  <Typography style={{ fontSize: 12, fontWeight: 700 }}>
                    {property.propertyName}
                  </Typography>
                  {!!property.clientName && (
                    <Typography style={{ fontSize: 10, color: '#777' }}>
                      {property.clientName}
                    </Typography>
                  )}
                  {property.areas.map(area => {
                    const id = String(area.sourceAreaId);
                    return (
                      <div className={classes.importRow} key={id}>
                        <Checkbox
                          size="small"
                          color="primary"
                          checked={selectedImportIds.includes(id)}
                          disabled={area.alreadyImported}
                          onChange={event => {
                            setSelectedImportIds(event.target.checked
                              ? [...selectedImportIds, id]
                              : selectedImportIds.filter(value => value !== id));
                          }}
                        />
                        <div className={classes.rowText}>
                          <div className={classes.primary}>{area.name || 'Area'}</div>
                          <div className={classes.secondary}>
                            {area.areaType || 'No area type'} · {formatSf(area.squareFeet)} SF
                            {area.sourceTakeoffName ? ` · ${area.sourceTakeoffName}` : ''}
                          </div>
                        </div>
                        {area.alreadyImported && <Chip size="small" label="Imported" />}
                      </div>
                    );
                  })}
                </Box>
              ))}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportOpen(false)}>Cancel</Button>
          <Button
            color="primary"
            variant="contained"
            startIcon={<GetAppIcon />}
            onClick={handleImport}
            disabled={!selectedImportIds.length || controller?.saving || importLoading}
          >
            Import {selectedImportIds.length || ''}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!detailsArea} onClose={() => setDetailsArea(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Shared work-area details</DialogTitle>
        <DialogContent>
          {details && (
            <>
              <TextField
                fullWidth
                label="Name"
                value={details.name}
                onChange={event => setDetails({ ...details, name: event.target.value })}
                margin="dense"
              />
              <TextField
                fullWidth
                label="Area type"
                value={details.areaType}
                onChange={event => setDetails({ ...details, areaType: event.target.value })}
                margin="dense"
              />
              <TextField
                fullWidth
                label="Services"
                helperText="Comma-separated; all included property slices inherit these services."
                value={details.servicesRequired}
                onChange={event => setDetails({ ...details, servicesRequired: event.target.value })}
                margin="dense"
              />
              <Box display="flex" style={{ gap: 10 }}>
                <TextField
                  label="Production rate"
                  type="number"
                  value={details.productionRateValue}
                  onChange={event => setDetails({ ...details, productionRateValue: event.target.value })}
                  margin="dense"
                />
                <TextField
                  label="Rate unit"
                  value={details.productionRateUnit}
                  onChange={event => setDetails({ ...details, productionRateUnit: event.target.value })}
                  margin="dense"
                />
              </Box>
              <Typography style={{ fontSize: 11, fontWeight: 700, marginTop: 14 }}>
                INCLUDED PROPERTIES
              </Typography>
              {members.map(member => {
                const id = memberIdOf(member);
                return (
                  <FormControlLabel
                    key={id}
                    control={(
                      <Checkbox
                        checked={details.includedJobSiteIds.includes(id)}
                        onChange={event => {
                          const next = event.target.checked
                            ? [...details.includedJobSiteIds, id]
                            : details.includedJobSiteIds.filter(value => value !== id);
                          setDetails({ ...details, includedJobSiteIds: next });
                        }}
                        color="primary"
                      />
                    )}
                    label={`${member.propertyName}${member.clientName ? ` · ${member.clientName}` : ''}`}
                  />
                );
              })}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsArea(null)}>Cancel</Button>
          <Button color="primary" variant="contained" onClick={handleSaveDetails} disabled={controller?.saving}>
            Save and recalculate
          </Button>
        </DialogActions>
      </Dialog>

      <SplitPropertyBoundaryDialog
        open={splitBoundaryOpen}
        boundaries={boundaries}
        members={members}
        jobSiteIds={duplicateBoundaryWarning?.jobSiteIds || []}
        saving={controller?.saving}
        onClose={() => setSplitBoundaryOpen(false)}
        onConfirm={controller?.saveMemberBoundaries}
      />
    </Box>
  );
};

export default OperationalPropertyGroupSection;
